import React, { useContext, useEffect, useRef, useState } from 'react';
import AuthHeader from '../commons/AuthHeader';
import AuthFooter from '../commons/AuthFooter';
import { Button, ButtonVariant, IconName, ButtonIconAlign, Icon } from '@eightfold.ai/octuple';
import { globalContext } from '../store';
import Api from '../Api';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import Loader from '../commons/Loader';
import { checkUnauthorized, checkAuthErr, getBrandValues, getBrandImages, getAssessmentDuration, applyClientBranding, applyGlobalBranding as applyGlobalBrandingCommon, showErrorSnackbar, launchAssessmentWithStatusUpdate, updateRaterStatus } from '../commons/commonFunctions';
import { useNavigate } from 'react-router-dom';
import { clientInfo } from '../commons/ClientDynamicInfo';
import SimpleBar from 'simplebar-react';
import 'simplebar-react/dist/simplebar.min.css';
import CompletedAssessmentsTableModal from '../commons/completedAssessmentsTableModal';
import { actionButtonStyle } from '../commons/commonStyles';

dayjs.extend(utc);
dayjs.extend(timezone);

const completedStatus = ['C', 'Completed'];
const completedPendingStatus = ['CP', 'Completed - Pending'];
const notStartedStatus = ['N', 'Not Started'];
const inProgressStatus = ['P', 'In Progress'];
const notInvitedStatus = ['NI', 'Not Invited'];
const ratersInvitedStatus = ['RI', 'Raters Invited'];

export const shouldKeepCompletedSelfThreeSixtyActive = (assessmentData: any): boolean => {
  const sourceStatus = assessmentData?.assessmentDetails?.source?.status;
  const raterType = assessmentData?.assessmentDetails?.source?.raterType;
  const isSelfRater = raterType === 'Self' || raterType === 'self';
  const isCompleted = completedStatus.includes(sourceStatus) ||
    completedPendingStatus.includes(sourceStatus) ||
    completedStatus.includes(assessmentData?.appraisalStatus) ||
    completedPendingStatus.includes(assessmentData?.appraisalStatus);

  return assessmentData?.category === '360' &&
    isSelfRater &&
    assessmentData?.survey?.allowToSelectRaters === true &&
    !assessmentData?.isThresholdMet &&
    isCompleted;
};

export const getAssessmentSourceKey = (assessmentData: any): string =>
  (assessmentData?.sourceName || assessmentData?.assessmentDetails?.source?.name || '').trim().toLowerCase();

const normalizeAssessmentValue = (value: any): string =>
  typeof value === 'string' ? value.trim().toLowerCase().replace(/[^a-z0-9]+/g, ' ') : '';

const getAssessmentSearchText = (assessmentData: any): string => [
  assessmentData?.title,
  assessmentData?.name,
  assessmentData?.category,
  assessmentData?.sourceName,
  assessmentData?.assessmentDetails?.source?.name,
  assessmentData?.assessmentDetails?.source?.category,
].map(normalizeAssessmentValue).filter(Boolean).join(' ');

// Dashboard instrument order, lowest rank renders first
export const CARD_GROUP_RANK = {
  SELF_360: 0,
  MAYO_360: 1,
  IMMERSIVE_META: 2,
  IMMERSIVE_AGILITY: 3,
  IMMERSIVE_CXO: 4,
  HOGAN_HPI: 5,
  HOGAN_HDS: 6,
  HOGAN_MVPI: 7,
  SAVILLE_WAVE: 8,
  HCA: 9,
  LEADERSHIP_SIGNATURE: 10,
  CULTURE_IMPACT: 11,
  SAVILLE_SWIFT: 12,
  HOGAN_BRI: 13,
  BUSINESS_IMPACT: 14,
  OTHER_VENDOR: 15,
  RATER: 16,
};

export const getAssessmentCardGroupRank = (assessmentData: any): number => {
  const raterType = assessmentData?.assessmentDetails?.source?.raterType || assessmentData?.raterType;
  const normalizedRaterType = normalizeAssessmentValue(raterType);
  const isSelfRater = !normalizedRaterType || normalizedRaterType === 'self';
  const category = normalizeAssessmentValue(assessmentData?.category);
  const isThreeSixty = category === '360';
  const sourceKey = getAssessmentSourceKey(assessmentData).replace(/\s+/g, '');
  const searchText = getAssessmentSearchText(assessmentData);
  if (!isSelfRater) return CARD_GROUP_RANK.RATER;
  if (isThreeSixty && sourceKey === 'heidrick') return CARD_GROUP_RANK.MAYO_360;
  if (isThreeSixty) return CARD_GROUP_RANK.SELF_360;
  if (category === 'meta') return CARD_GROUP_RANK.IMMERSIVE_META;
  if (category === 'agility') return CARD_GROUP_RANK.IMMERSIVE_AGILITY;
  if (category === 'cxo') return CARD_GROUP_RANK.IMMERSIVE_CXO;
  if (/\b(hpi|hogan potential inventory)\b/.test(searchText)) return CARD_GROUP_RANK.HOGAN_HPI;
  if (/\b(hds|hogan development survey)\b/.test(searchText)) return CARD_GROUP_RANK.HOGAN_HDS;
  if (/\b(mvpi|hogan motives values and potential inventory)\b/.test(searchText)) return CARD_GROUP_RANK.HOGAN_MVPI;
  if (/\bsaville wave\b/.test(searchText)) return CARD_GROUP_RANK.SAVILLE_WAVE;
  if (category === 'hca' || category === "hca short form" || category === 'hca long form' || /\bheidrick capabilities assessment\b/.test(searchText)) return CARD_GROUP_RANK.HCA;
  if (/\bleadership signature\b/.test(searchText)) return CARD_GROUP_RANK.LEADERSHIP_SIGNATURE;
  if (/\bculture impact\b/.test(searchText)) return CARD_GROUP_RANK.CULTURE_IMPACT;
  if (/\bsaville swift\b/.test(searchText)) return CARD_GROUP_RANK.SAVILLE_SWIFT;
  if (/\b(hogan bri|bri)\b/.test(searchText)) return CARD_GROUP_RANK.HOGAN_BRI;
  if (category === 'impact' || /\bbusiness impact\b/.test(searchText)) return CARD_GROUP_RANK.BUSINESS_IMPACT;

  return CARD_GROUP_RANK.OTHER_VENDOR;
};

export const compareAssessmentCards = (assessmentA: any, assessmentB: any): number => {
  const rankA = getAssessmentCardGroupRank(assessmentA);
  const rankB = getAssessmentCardGroupRank(assessmentB);
  if (rankA !== rankB) return rankA - rankB;

  if (rankA === CARD_GROUP_RANK.OTHER_VENDOR) {
    const sourceA = getAssessmentSourceKey(assessmentA);
    const sourceB = getAssessmentSourceKey(assessmentB);
    if (sourceA !== sourceB) return sourceA.localeCompare(sourceB);
  }

  return getAssessmentDisplayTitle(assessmentA).localeCompare(getAssessmentDisplayTitle(assessmentB));
};

export const getAssessmentDisplayTitle = (assessmentData: any): string => {
  const title = assessmentData?.title || '';
  const isThreeSixty = assessmentData?.category === '360';
  const isInHouseImpact = assessmentData?.category === 'Impact' && assessmentData?.sourceName === 'Heidrick';
  const raterType = assessmentData?.assessmentDetails?.source?.raterType || assessmentData?.raterType || assessmentData?.type;
  const isSelfRater = raterType === 'Self' || raterType === 'self';
  if (isInHouseImpact) {
    return assessmentData?.name || title;
  }
  if (!isThreeSixty) {
    return title;
  }

  const surveyTitle = isSelfRater
    ? assessmentData?.survey?.surveyNameLeaders
    : assessmentData?.survey?.surveyNameRaters;
  const trimmedSurveyTitle = typeof surveyTitle === 'string' ? surveyTitle.trim() : '';

  return trimmedSurveyTitle || title;
};

/**
 * Returns true only if `url` is a safe relative path:
 * starts with '/', contains no protocol or protocol-relative prefix.
 * This prevents open-redirect via path injection into a base URL.
 */
export const isSafeRelativePath = (url: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim();
  return (
    trimmed.startsWith('/') &&
    !trimmed.startsWith('//') &&
    !/^[a-zA-Z][a-zA-Z0-9+\-.]*:/i.test(trimmed) //NOSONAR
  );
};

const formatDate = (dateString: any) => {
  const formatedDate = dayjs(dateString).format('MMM D, YYYY');
  return formatedDate;
};

const getMinutesDifference = (date1: any) => {
  const updatedDate = dayjs.utc(date1);
  const date2 = dayjs.utc(new Date());
  const diffInMinutes = date2.diff(updatedDate, 'minute'); // Output: 4
  return diffInMinutes;
};

const DUE_SOON_WINDOW_DAYS = 5;

const stripTime = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const pluralDays = (days: number) => (days === 1 ? 'Day' : 'Days');

/**
 * Returns the due-date pill label, or '' when no indicator should be shown
 * (no/invalid date, or the date is more than 5 days away).
 */
export const getDueDateLabel = (dueDateValue: any, today: Date = new Date()): string => {
  if (dueDateValue === null || dueDateValue === undefined || dueDateValue === '') {
    return '';
  }

  const dueDate = new Date(dueDateValue);
  if (isNaN(dueDate.getTime())) {
    return '';
  }

  const strippedDueDate = stripTime(dueDate);
  const strippedToday = stripTime(today);
  const dayDiff = Math.round((strippedDueDate.getTime() - strippedToday.getTime()) / (1000 * 60 * 60 * 24));

  if (dayDiff < 0) {
    const overdueDays = Math.abs(dayDiff);
    return `Overdue by ${overdueDays} ${pluralDays(overdueDays)}`;
  }

  if (dayDiff === 0) {
    return 'Due today';
  }

  if (dayDiff <= DUE_SOON_WINDOW_DAYS) {
    return `Due in ${dayDiff} ${pluralDays(dayDiff)}`;
  }

  return '';
};

const EmptyPill = () => (
  <div className='cardPillWrap'>
    <div className=''>&nbsp;</div>
    <div className='cardPillTxt'>&nbsp;</div>
  </div>
);

export const GeneratePill = (props: any) => {
  const assessmentsData = props?.assessmentsData;

  const isCompleted = completedStatus.includes(assessmentsData?.assessmentDetails?.source?.status);
   

  const pillLabel = getDueDateLabel(assessmentsData?.dueDate);
  if (!pillLabel || isCompleted) {
    return <EmptyPill />;
  }

  return (
    <div className='cardPillWrap'>
      <div className='cardPill Due'></div>
      <div className='cardPillTxt'>{pillLabel}</div>
    </div>
  );
};

export const openAssessments = (url: string, navigate: any, raterDetails?: any, dispatch?: any, assessmentsData?: any, setAssessmentActionLoader?: any) => {
  const payloadData = {
    immersiveAssessmentUrl : assessmentsData?.assessmentDetails?.source?.url,
    assessmentId: assessmentsData?.assessmentId,
    assignmentId: assessmentsData?.assignmentId,
    immersiveVideoPath: assessmentsData?.videoPath,
    assessmentStatus : assessmentsData?.assessmentDetails?.source?.status,
    prepWorkCompleted: assessmentsData?.assessmentDetails?.prepWorkCompleted,
    projectAssignmentsData : {
      projectId: assessmentsData?.assessmentDetails?.projectId,
      leaderProfileId: assessmentsData?.assessmentDetails?.leaderProfileId,
      assessments: [
        {
          "assessmentStructureId": assessmentsData?.assessmentStructureId,
          "assignmentStatus": "In Progress",
        }
      ]
    },
    assessmentsData:{
      clientId: assessmentsData?.clientId,
      leaderProfileId: assessmentsData?.assessmentDetails?.leaderProfileId,
      source:{
        ...(assessmentsData?.assessmentDetails?.source || {}),
        status: "P",
      }
    }
  };

  if (url === 'inviteRaters') {
    const route = '/raterSurveyWelcome';
    navigate(route, {
      state: { raterDetails: raterDetails },
    });
  } else if(url === 'immersiveAgility'){
    let route = '/immersive/introduction';
    if(assessmentsData?.assessmentDetails?.source?.status === 'N'){
      const payloadDataImmersive = {
        ...payloadData,
        prepData: {
          ...assessmentsData?.sections?.[0],
          assessmentType: assessmentsData?.category,
          assessmentTitle: assessmentsData?.title,
          preReadAssetUrl : assessmentsData?.assessmentDetails?.source?.category === 'CXO' ? assessmentsData?.assessmentDetails?.source?.url?.replace('/watch/', '/pre-read/') : undefined,
        }
      }
      dispatch({ type: 'SET_IMMERSIVE_ASSESSMENT_URL', payload: payloadDataImmersive });
      navigate(route);
    } else if(assessmentsData?.assessmentDetails?.source?.status === 'P'){
      const payloadData = {
        immersiveAssessmentUrl : assessmentsData?.assessmentDetails?.source?.url,
        assessmentId: assessmentsData?.assessmentId,
        assessmentStatus : assessmentsData?.assessmentDetails?.source?.status,
        immersiveVideoPath: assessmentsData?.videoPath,
        prepWorkCompleted: assessmentsData?.assessmentDetails?.prepWorkCompleted,
        prepData: {
          ...assessmentsData?.sections?.[0],
          assessmentType: assessmentsData?.category,
          assessmentTitle: assessmentsData?.title,
          preReadAssetUrl : assessmentsData?.assessmentDetails?.source?.category === 'CXO' ? assessmentsData?.assessmentDetails?.source?.url?.replace('/watch/', '/pre-read/') : undefined,
        }
      }
      
      dispatch({ type: 'SET_IMMERSIVE_ASSESSMENT_URL', payload: payloadData });
      navigate(route);
    }
   
  } else {
    if(assessmentsData?.assessmentDetails?.source?.name === 'Hogan' || assessmentsData?.assessmentDetails?.source?.name === 'HOGAN'){
      openHoganAssessment(url, setAssessmentActionLoader, assessmentsData, dispatch, payloadData);
    } else if (assessmentsData?.category === 'Impact' && assessmentsData?.sourceName === 'Heidrick') {
      const assessmentId = assessmentsData?.assessmentId;
      const impactRoute = `/business-impact/${assessmentId}`;
      const currentStatus = assessmentsData?.assessmentDetails?.source?.status;
      if (notStartedStatus.includes(currentStatus)) {
        setAssessmentActionLoader(true);
        launchAssessmentWithStatusUpdate(payloadData, setAssessmentActionLoader, dispatch, true)
          ?.then(() => {
            setAssessmentActionLoader(false);
            navigate(impactRoute, { state: { assessmentsData } });
          });
      } else {
        navigate(impactRoute, { state: { assessmentsData } });
      }
    } else if (assessmentsData?.category !== '360') {
      launchAssessmentWithStatusUpdate(payloadData, setAssessmentActionLoader, dispatch);
    } else if (assessmentsData?.category === '360') {
      setAssessmentActionLoader(true);
      const isHeidrick = assessmentsData?.sourceName === 'Heidrick' || assessmentsData?.assessmentDetails?.source?.name === 'Heidrick';
      const assessmentUrl =  isHeidrick ? `/assessment${assessmentsData?.assessmentDetails?.source?.url}` : assessmentsData?.assessmentDetails?.source?.url;
      let isHeidrickData = {};
      if( isHeidrick ){
        isHeidrickData = {
          assessmentId: raterDetails?.assignmentData?.assessmentId,
          assessmentStructureId: raterDetails?.assignmentData?.assessmentStructureId,
          surveyTemplate: raterDetails?.assignmentData?.surveyTemplate,
          surveyNameLeaders: raterDetails?.assignmentData?.surveyNameLeaders,
          surveyNameRaters: raterDetails?.assignmentData?.surveyNameRaters,
          isSelfAssessment: assessmentsData?.assessmentDetails?.source?.raterType === 'Self',
          leaderName: assessmentsData?.leaderName,
        };
      }

      const currentStatus = assessmentsData?.assessmentDetails?.source?.status;
      if (inProgressStatus.includes(currentStatus)) {
        // Status already In Progress — skip redundant API call and navigate directly
        setAssessmentActionLoader(false);
        if (isHeidrick) {
          navigate(assessmentUrl, { state: { raterDetails: { ...isHeidrickData } } });
        } else {
          window.open(assessmentUrl, '_self');
        }
      } else {
        updateRaterStatus({
          assignmentId: assessmentsData?.assignmentId,
          assessmentStructureId: assessmentsData?.assessmentStructureId,
          status: "In Progress",
          raterId: assessmentsData?._id,
          showNotifications: false,
          url: assessmentUrl,
          isInhouse: isHeidrick,
          isHeidrickData: isHeidrickData,
          navigate: navigate
        });
      }
    } 
    else {
      window.open(url, '_self');
    }
  }
};

export const openHoganAssessment = async (url: string, setAssessmentActionLoader: any, assessmentsData: any, dispatch: any, payloadData: any) => {
  try {
    setAssessmentActionLoader(true);
    if (!isSafeRelativePath(url)) {
      showErrorSnackbar({ content: 'Invalid assessment URL path' });
      setAssessmentActionLoader(false);
      return;
    }
    const fullUrl = (process.env.REACT_APP_PLATFORM_BASE_URL ?? '') + url;
    
    const response = await fetch(fullUrl, {
      method: 'GET',
      ...Api.getAuthOptions(),
    });

    if (!response.ok) {
      showErrorSnackbar({ content: 'Error occurred while performing this operation.' });
      setAssessmentActionLoader(false);
      return;
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    // Only allow valid blob URLs
    if (typeof blobUrl === 'string' && blobUrl.startsWith('blob:')) {
      const link = document.createElement('a');
      link.setAttribute('href', blobUrl);
      if (assessmentsData?.assessmentDetails?.source?.status === 'N') {
        const launchAssessment = await launchAssessmentWithStatusUpdate(payloadData, setAssessmentActionLoader, dispatch, true);
        if(launchAssessment) {
          // Click detached anchor — no DOM append needed, avoids XSS vector
          link.click();
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
          setAssessmentActionLoader(false);
        }
      } else {
        link.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(blobUrl);
        }, 100);
        setAssessmentActionLoader(false);
      }
      
    } else {
      showErrorSnackbar({ content: 'Failed to create a valid blob URL' });
      setAssessmentActionLoader(false);
    }
  } catch (error) {
    showErrorSnackbar({ content: 'Error occurred while performing this operation.' });
    setAssessmentActionLoader(false);
  }
};

export const GenerateProgressIcon = (props: any) => {
  const { isThreeSixtySelfAssessment, ratersInvitedStatus, assessmentSourceStatus, isMetaAgilityOrCxo, notStartedStatus, assessmentsData, inProgressStatus, completedStatus, completedPendingStatus, callbackAssessmentStatusForHogan } = props;
  const comletedStatuses = [...completedStatus, ...completedPendingStatus];
  if (isThreeSixtySelfAssessment && ratersInvitedStatus.includes(assessmentSourceStatus)) {
    return ( 
      <>
        {'Raters Nominated'}
        <Icon
          classNames="dueIcon"
          data-test-id="myIconTestId"
          description="My icon description."
          id="myIconId"
          path={IconName.mdiAccountMultiple}
          role="presentation"
          rotate={0}
          size="20px"
          title="My icon title."
        />
      </>
    );
  } else if (isMetaAgilityOrCxo && notStartedStatus.includes(assessmentSourceStatus) && assessmentsData?.assessmentDetails?.prepWorkCompleted === true) {
    return (
      <>
        {'Prep Completed'}
        <Icon
          classNames="dueIcon"
          data-test-id="myIconTestId"
          description="My icon description."
          id="myIconId"
          path={IconName.mdiCheck}
          role="presentation"
          rotate={0}
          size="20px"
          title="My icon title."
        />
      </>
    );
  } else if ((isThreeSixtySelfAssessment && comletedStatuses.includes(assessmentSourceStatus)) || (props?.urlAttachedId === props?.assessmentsData?.assessmentId && (callbackAssessmentStatusForHogan === 'Completed' || callbackAssessmentStatusForHogan === '')) ){
    return ( 
      <>
        {'Survey Completed'}
        <Icon
          classNames="dueIcon"
          data-test-id="myIconTestId"
          description="My icon description."
          id="myIconId"
          path={IconName.mdiCheck}
          role="presentation"
          rotate={0}
          size="20px"
          title="My icon title."
        />
      </>
    );
  } else {
    return (
      <>
        {(inProgressStatus.includes(assessmentSourceStatus)) ? 'In Progress' : 'Not Started'}
        <div className={`cardProgress ${inProgressStatus.includes(assessmentSourceStatus) && 'InProgress'}`}></div>
      </>
    );
  }
}

// Removed mutable module-level counts; counts will be recomputed via useEffect
export const GenerateCards = (props: any) => {
  const params = new URLSearchParams(window.location.search);
  const urlAssessmentId = params.get('assessmentId');
  const callbackAssessmentStatusForHogan = params.get('AssessmentStatus');
  const assessmentsData: any = props?.assessmentsData;
  const assessmentSourceStatus = assessmentsData?.assessmentDetails?.source?.status;
  const itemRefs = useRef(new Map());
  const [moreBtnText, setMoreBtnText] = useState('more');
  const [moreBtnIcon, setMoreBtnIcon] = useState(IconName.mdiPlus);
  const [assessmentActionLoader, setAssessmentActionLoader] = useState(false);
  let assessmentUrl = assessmentsData?.assessmentDetails?.source?.url;
  const completedStatuses = [...completedStatus, ...completedPendingStatus];
  const payloadData = {
    assignmentId: assessmentsData?.assignmentId,
    assessmentId: assessmentsData?.assessmentId,
    clientId: assessmentsData?.assessmentDetails?.clientId,
    appraisalID: assessmentsData?.sourceAssessmentId,
    focusFirstName: props?.leaderData?.leaderFirstName,
    focusLastName: props?.leaderData?.leaderLastName,
    focusEmail: props?.leaderData?.leaderEmail,
    accountID: props?.clientDetails?.applications?.insights?.accountId,
    focusLeaderProfileId: props?.leaderData?.leaderProfileId,
    assessmentStructureId: assessmentsData?.assessmentStructureId,
    projectId: assessmentsData?.assessmentDetails?.projectId,
    projectName: props?.leaderData?.projectName,
    assessmentName: assessmentsData?.name,
    assessmentDueDate: assessmentsData?.dueDate,
    emailTemplates: props?.leaderData?.emailTemplates,
    invite: true,
    sourceName: assessmentsData?.sourceName || assessmentsData?.assessmentDetails?.source?.name,
    surveyTemplate: assessmentsData?.assessmentDetails?.source?.raterType === 'Self' 
      ? assessmentsData?.survey?.surveyTemplate 
      : assessmentsData?.raterGroups?.find((rg: any) => rg.name === assessmentsData?.assessmentDetails?.source?.raterType)?.template,
    surveyNameLeaders: assessmentsData?.survey?.surveyNameLeaders,
    surveyNameRaters: assessmentsData?.survey?.surveyNameRaters,
  };
  let raterDetails = {
    ratersConfig: assessmentsData?.raterGroups && [...assessmentsData.raterGroups].sort((a: any, b: any) => (a.order || 0) - (b.order || 0)),
    ratersData: assessmentsData?.raters,
    assignmentData: payloadData,
    projectEndDate: assessmentsData?.dueDate,
    assessmentUrl: assessmentUrl,
  };
  const dueDateText = assessmentsData?.dueDate ? formatDate(assessmentsData?.dueDate) : '';
  const isMetaAgilityOrCxo = (assessmentsData?.category === 'Agility' || assessmentsData?.category === 'META' || assessmentsData?.category.toLowerCase() === 'cxo');
  let btnTxt = '';
  if (notStartedStatus.includes(assessmentSourceStatus) && !isMetaAgilityOrCxo) {
    btnTxt = 'Begin';
  } else if (notStartedStatus.includes(assessmentSourceStatus) && assessmentsData?.assessmentDetails?.prepWorkCompleted === true && isMetaAgilityOrCxo) {
    btnTxt = 'Review Prep';
  } else if (notStartedStatus.includes(assessmentSourceStatus) && isMetaAgilityOrCxo) {
    btnTxt = 'Begin Prep';
  } else if (inProgressStatus.includes(assessmentSourceStatus)) {
    btnTxt = 'Continue';
  }

  const isThreeSixtySelfAssessment = (assessmentsData?.category === '360' && assessmentsData?.assessmentDetails?.source?.raterType === 'Self');
  const isRaterAssessment = (assessmentsData?.category === '360' && assessmentsData?.assessmentDetails?.source?.name !== 'Heidrick' &&  assessmentsData?.assessmentDetails?.source?.raterType !== 'Self');
  const isMayoRaterAssessment = (assessmentsData?.category === '360' && assessmentsData?.assessmentDetails?.source?.name === 'Heidrick' && assessmentsData?.assessmentDetails?.source?.raterType !== 'Self');
  let assessmentCategory = assessmentsData?.category;
  if (isRaterAssessment) {
    assessmentCategory = '360-Raters';
  } else if (isMayoRaterAssessment) {
    // keep the original category for Heidrick rater assessments
    assessmentCategory = assessmentsData?.category;
  }

  if(isMetaAgilityOrCxo && (notStartedStatus.includes(assessmentSourceStatus) || inProgressStatus.includes(assessmentSourceStatus))) {
    assessmentUrl = 'immersiveAgility';
  }

  if (assessmentsData?.category === '360' && assessmentsData?.assessmentDetails?.source?.raterType === 'Self') {

    if (assessmentsData?.survey?.allowToSelectRaters) {
      assessmentUrl =
        // assessmentsData?.assessmentDetails?.source?.status === 'P' 
        //   ? assessmentsData?.assessmentDetails?.source?.url
          // : 
          'inviteRaters';
    }

    if ((notStartedStatus.includes(assessmentSourceStatus) || notInvitedStatus.includes(assessmentSourceStatus)) && assessmentsData?.survey?.allowToSelectRaters ) {
      // Check raters array length to determine button text
      if (assessmentsData?.raters?.length === 1) {
        btnTxt = 'Begin Prep';
      } else if (assessmentsData?.assessmentId === urlAssessmentId) {
        btnTxt = 'Review';
      } else if (assessmentsData?.raters?.length > 1) {
        btnTxt = 'Continue';
      } 
    } 
    else if ((notStartedStatus.includes(assessmentSourceStatus) || notInvitedStatus.includes(assessmentSourceStatus)) && !assessmentsData?.survey?.allowToSelectRaters ){
      btnTxt = (assessmentsData?.survey?.allowToSelectRaters) ? 'Begin Prep' : 'Begin';
    } else if (inProgressStatus.includes(assessmentSourceStatus) && assessmentsData?.assessmentId !== urlAssessmentId) {
      btnTxt = 'Continue';
    } else if (ratersInvitedStatus.includes(assessmentSourceStatus) && assessmentsData?.assessmentId !== urlAssessmentId) {
      btnTxt = 'Continue';
    } else if (completedStatuses.includes(assessmentSourceStatus) || assessmentsData?.assessmentId === urlAssessmentId) {
      btnTxt = 'Review';
    }
  }

  const setItemRef = (id :string ) => (element:any) => {
    if (element) {
      itemRefs.current.set(id, element);
    } else {
      itemRefs.current.delete(id);
    }
  }

  const toggleClass = (id: string) => {
    const element = itemRefs.current.get(id);
    
    if (element) {
      // Toggle the 'highlight' class
      element.querySelector('.cardTitle')?.classList.toggle('hideTitle');
      element.querySelector('.cardDescription')?.classList.toggle('showDescription');
      setMoreBtnText((prevText) => (prevText === 'more' ? 'less' : 'more'));
      setMoreBtnIcon((prevIcon) => (prevIcon === IconName.mdiPlus ? IconName.mdiMinus : IconName.mdiPlus));
    }
  }

  const getCardDescription = () => {
    if (isThreeSixtySelfAssessment) {
      return assessmentsData?.descriptionSelf;
    }
    if (isRaterAssessment || isMayoRaterAssessment) {
      return assessmentsData?.descriptionRater;
    }
    return assessmentsData?.description;
  };

  return (
    <div 
      className={`dashboardCardWrapper ${isRaterAssessment || isMayoRaterAssessment ? 'raterCardWrapper' : ''}`}
      style={{width: (props?.assessmentCounts?.all < 2) ? '45%' : '32%'}}
    >
      <div className='dashboardBodyWrap'>
        <div className='dashboardCardHeader'>
          <GeneratePill assessmentsData={assessmentsData} />
          <div className='cardProgressWrap'>
            <GenerateProgressIcon 
              isThreeSixtySelfAssessment={isThreeSixtySelfAssessment}
              ratersInvitedStatus={ratersInvitedStatus}
              assessmentSourceStatus={assessmentSourceStatus}
              isMetaAgilityOrCxo={isMetaAgilityOrCxo}
              notStartedStatus={notStartedStatus}
              assessmentsData={assessmentsData}
              inProgressStatus={inProgressStatus}
              completedStatus={completedStatus}
              completedPendingStatus={completedPendingStatus}
              urlAttachedId={urlAssessmentId}
              callbackAssessmentStatusForHogan={callbackAssessmentStatusForHogan}
            />
          </div>
        </div>
        <div className='dashboardCardBody' ref={setItemRef(props.cardId)}>
          <div className="cardTitle">
            {getAssessmentDisplayTitle(assessmentsData)}
            {(isRaterAssessment || isMayoRaterAssessment) && <div className='raterName'>{assessmentsData?.leaderName}</div>}
          </div>
          <div className='cardDescription'>
            <SimpleBar style={{ maxHeight: '175px' }} autoHide={false} className="descriptionScroll">
              {getCardDescription()} 
            </SimpleBar>
          </div>
          
          <div className='cardAction'>

            {(assessmentsData) && (
              <Button
                text={btnTxt}
                variant={ButtonVariant.Secondary}
                classNames='cardActionButton'
                onClick={() => openAssessments(assessmentUrl, props?.navigate, raterDetails, props?.dispatch, assessmentsData, setAssessmentActionLoader)}
               // iconProps={{ path: IconName.mdiArrowRight }}
                alignIcon={ButtonIconAlign.Right}
                iconProps={assessmentActionLoader ? { path: IconName.mdiLoading, spin: true } : { path: IconName.mdiArrowRight }}
                disabled={assessmentActionLoader}
              />
            )}
            <Button
              text={moreBtnText}
              variant={ButtonVariant.Secondary}
              classNames='cardMoreBtn'
              onClick={() => toggleClass(props.cardId)}
              transparent={true}
              iconProps={{ path: moreBtnIcon }}
              alignIcon={ButtonIconAlign.Left}
            />
          </div>
        </div>
      </div>
      <div className='dashboardCardFooter'>
        <hr className='cardSeprator'></hr>
        <div className='footerActions'>
          <div className='cardDueDate'>
            <div className='dueDateTxt'>{`Due: ${dueDateText}`}</div>
          </div>
          {(getAssessmentDuration(assessmentCategory)) &&
            <div className='assessmentTime'>
              <div className='time'> 
                {(assessmentsData?.category === 'Impact' && assessmentsData?.sourceName === 'Heidrick') ? (
                  '~' + assessmentsData?.sections?.find((s: any) => s?.type === 'Introduction')?.pages[0]?.metadata?.estimatedDurationMinutes?.value
                ) : (
                  getAssessmentDuration(assessmentCategory)
                )}
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

const DashBoard = () => {
  const navigate = useNavigate();
  const { globalState, dispatch } = useContext(globalContext);
  const title = globalState?.clientData?.name;
  const [assignedAssessments, setAssignedAssessments] = useState<any>([]);
  const [showLoader, setShowLoader] = useState(true);
  const [leaderName, setLeaderName] = useState<any>('');
  const [headerLogo, setHeaderLogo] = useState<string>('');
  const [completedAssessArr, setCompletedAssessArr] = useState<any>([]);
  const [closedAssessArr, setClosedAssessArr] = useState<any>([]);
  const [documentsData, setDocumentsData] = useState<any>([]);
  const [headerImage, setHeaderImage] = useState<string>('');
  const [, setPrimaryColor] = useState<string>('#BA3A19');
  const cardWrapRef = useRef<HTMLDivElement>(null); 
  const [assessmentCounts, setAssessmentCounts] = useState<any>({ all: 0, completed: 0, closed: 0 });
  const [showModal, setShowModal] = useState(false);
  const [, setLeftPanelImg] = useState<string>('');

  const noActiveAssessments = "You don't have any active assignments right now.";
  const noActiveAssessmentsDesc = "You'll receive an email when your next assignment is available.";
  const noActiveAssessmentImg = 'assets/img/empty-drawer.svg';
  const noAssessmentsAssigned = "You don't have any assignments yet.";
  const noAssessmentsAssignedDesc = "You'll receive an email when your first assignment is available.";
  const noAssessmentsAssignedImg = 'assets/img/waiting-assignment.svg';

  const [isMobile, setIsMobile] = useState(window.innerWidth < 450);
  const [isLarge, setIsLarge]  = useState(window.innerWidth < 1400);
  const applyGlobalBranding = () => {
    const leftPanelImgId = getBrandValues(globalState?.clientData?.branding, 'WELCOME_PAGE_IMAGE');

    if (globalState?.leftPanelImg) {
      setLeftPanelImg(globalState.leftPanelImg);
    } else {
      getBrandImages(leftPanelImgId, setLeftPanelImg, 'leftImage', dispatch, 'SET_LEFT_PANEL_IMG');
    }
    applyGlobalBrandingCommon(globalState, setHeaderLogo, setHeaderImage, setPrimaryColor, dispatch);
    
  }

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
      setIsLarge(window.innerWidth < 1400);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const params = new URLSearchParams(window.location.search);
  const hasAssessmentCallback = params.has('status') && params.has('assessmentId');
  const urlAssessmentId = hasAssessmentCallback ? params.get('assessmentId') ?? '' : '';
  const callbackAssessmentStatusForHogan = hasAssessmentCallback && params.has('AssessmentStatus')
    ? params.get('AssessmentStatus') ?? ''
    : '';
  useEffect(() => {
    if (Object.keys(globalState?.leaderData).length === 0) {
      getLeaderData();
    } else {
      if (!globalState?.leaderData?.isProfileCompleted) {
        navigate('/welcome');
      }
      setLeaderName(globalState?.leaderData?.firstName);
      setAssignedAssessments([]);
      getLeaderProjects(globalState?.leaderData?.id, globalState?.leaderData?.email);
      getLeaderReports(globalState?.leaderData?.id);
    }

    if (title && clientInfo.hasOwnProperty(title)) {
      applyClientBranding(setHeaderImage, setHeaderLogo, title);
    } else {
      applyGlobalBranding();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const commonApiCall = (response: any) => {
    const res = response?.response?.data?.data?.[0];
    dispatch({
      type: 'SET_LEADER_DATA',
      payload: res,
    });
    if (!res?.isProfileCompleted) {
      navigate('/welcome');
    }
    const leaderProfileId = response?.response?.data?.data[0]?.id;
    const leaderEmail = response?.response?.data?.data[0]?.email;
    setLeaderName(response?.response?.data?.data[0]?.firstName);
    setAssignedAssessments([]);
    getLeaderProjects(leaderProfileId, leaderEmail);
    getLeaderReports(leaderProfileId);
  };

  const getLeaderData = () => {
    let loginToken: any = JSON.parse(sessionStorage.getItem('loginToken') ?? 'false');

    const payload = {
      email: loginToken?.idToken?.claims?.email.toLowerCase(),
      clientId: globalState?.clientData?.id,
    };

    Api.getLeaderProfile(payload)
      .then(response => {
        if (response?.success) {
          if (response && response?.response?.data && response?.response?.data?.data.length > 0) {
            commonApiCall(response);
          }
        }
        checkUnauthorized(response, dispatch);
      })
      .catch((err: any) => {
        console.log(err);
        checkAuthErr(err, dispatch);
      });
  };

  const getLeaderProjects = (leaderProfileId: any, leaderEmail: string) => {
    let idsString;
    Api.getLeaderProjects(globalState?.clientData?.id, leaderProfileId)
      .then((response: any) => {
        if (response?.success) {
          const allAssessmentsRecord = response?.response?.data?.data;
          let ratersAssessmentsList: any = [];
          let assessmentStructureIds: any = [];
          if (allAssessmentsRecord.length > 0) {
            allAssessmentsRecord.forEach((project: any) => {
              let assessmentAsRaterList: any = [];
              let assessmentAsSelfList: any = [];
              let leaderDetails: any = [];
              project?.assignments?.forEach((assignment: any) => {
                leaderDetails = {
                  'autoProvisioned': assignment?.autoProvisioned,
                  'clientId': assignment?.clientId,
                  'id': assignment?.id,
                  'isManualAssigned': assignment?.isManualAssigned,
                  'leaderEmail': assignment?.leaderEmail,
                  'leaderFirstName': assignment?.leaderFirstName,
                  'leaderLastName': assignment?.leaderLastName,
                  'leaderProfileId': assignment?.leaderProfileId,
                  'projectId': assignment?.projectId,
                  'projectName': project?.name,
                  'emailTemplates': project?.emailTemplates,
                }
                assignment?.assessments?.forEach((assessment: any) => {
                  const loggedInRater = assessment?.raters?.find((rater: any) => rater?.raterEmail === leaderEmail);
                  // Use the logged-in user's individual rater status when available; fall back to overall assignmentStatus
                  const isNotInvitedForUser = loggedInRater
                    ? loggedInRater?.status === 'Not Invited'
                    : assessment?.assignmentStatus === 'Not Invited';
                  if (!isNotInvitedForUser) {
                    if (assessment?.raters) {
                      const assessmentAsRater = assessment?.raters.find((rater: any) => (rater?.raterEmail === leaderEmail && (rater?.type !== 'self' && rater?.type !== 'Self')));
                      const assessmentAsSelf = assessment?.raters.find((rater: any) => (rater?.raterEmail === leaderEmail && (rater?.type === 'self' || rater?.type === 'Self')));

                      if (assessmentAsRater && assessmentAsRater !== null && assessmentAsRater !== undefined) {
                        assessmentStructureIds.push(assessment?.assessmentStructureId);
                        assessmentAsRaterList.push({
                          'assessmentStructureId': assessment?.assessmentStructureId,
                          'dueDate': assessment?.dueDateTs ? assessment?.dueDateTs : project?.closeDate,
                          assignmentId: assignment?.id,
                          'leaderDetails': { ...leaderDetails },
                          ...assessmentAsRater,
                          'ignoreCloseDate': project?.ignoreCloseDate,
                          raterAssessmentStatus: assessmentAsRater?.status,
                          raterType: assessmentAsRater?.type,
                        });
                      }
                      if (assessmentAsSelf && assessmentAsSelf !== null && assessmentAsSelf !== undefined) {
                        assessmentStructureIds.push(assessment?.assessmentStructureId);
                        assessmentAsSelfList.push({
                          'assessmentStructureId': assessment?.assessmentStructureId,
                          ...assessmentAsSelf,
                          appraisalStatus: assessment?.appraisalStatus,
                          'dueDate': assessment?.dueDateTs ? assessment?.dueDateTs : project?.closeDate,
                          assignmentId: assignment?.id,
                          'leaderDetails': { ...leaderDetails },
                          'ignoreCloseDate': project?.ignoreCloseDate,
                          'ratersRequired': { ...assessment?.ratersRequired },
                          'raters': [...assessment?.raters],
                        });
                      }
                    } else {
                      assessmentStructureIds.push(assessment?.assessmentStructureId);
                      assessmentAsSelfList.push({
                        ...assessment,
                        'ignoreCloseDate': project?.ignoreCloseDate,
                        'dueDate': assessment?.dueDateTs ? assessment?.dueDateTs : project?.closeDate,
                        assignmentId: assignment?.id,
                      });
                    }
                  }
                });
              });
              ratersAssessmentsList.push({
                'projectDetails': {
                  'addedTs': project?.addedTs,
                  'closeDate': project?.closeDate,
                  'id': project?.id,
                  'startDate': project?.startDate,
                  'status': project?.status,
                  'clientId': project?.clientId,
                  'ignoreCloseDate': project?.ignoreCloseDate
                },
                'allassessmentsList': [...assessmentAsRaterList, ...assessmentAsSelfList],
              });
            });
          } else {
            setShowLoader(false);
            return;
          }

          idsString = assessmentStructureIds.filter((item: any) => item).join(',');
          const limitArray = idsString.split(',');
          if (idsString !== '') {
            Api.getLeaderAssessmentStructureDetails(idsString, limitArray?.length)
              .then((res: any) => {
                getAssessmentStructureDetails(ratersAssessmentsList, res?.response?.data?.data, leaderProfileId, leaderEmail);
              }).catch((err) => {
                checkAuthErr(err, dispatch);
              });
          } else {
            setShowLoader(false);
          }
        } else {
          setShowLoader(false);
        }
        checkUnauthorized(response, dispatch);
      })
      .catch(err => {
        checkAuthErr(err, dispatch);
      });
  };
  const getLeaderReports = (leaderProfileId: any) => {
    Api.getLeaderReports(leaderProfileId)
      .then((response: any) => {
        if (response?.success) {
          if (response && response?.response?.data?.data?.length > 0) {
            const res = response?.response?.data?.data;
            const filteredData = res.filter((item: any) => item.isLeaderEnabled === true);
            filteredData.sort((a: any, b: any) => {
              const dateA = new Date(a.reportDate);
              const dateB = new Date(b.reportDate);

              switch (dateA > dateB) {
                case true:
                  return -1;
                case false:
                  return 1;
              }
              return a.name.localeCompare(b.name);
            });
            setDocumentsData(filteredData);
          }
        }
        checkUnauthorized(response, dispatch);
      })
      .catch((err) => {
        checkAuthErr(err, dispatch);
      });
  };

  const pushIfNotEmpty = (targetArr: any[], items: any, filteredData: any[]) => {
    if (filteredData && filteredData.length > 0) {
      targetArr.push({ projectDetails: items?.projectDetails, allassessmentsList: [...filteredData] });
    }
  };

  const filteredCompletedAssessments = (finalUpdatedData: any) => {
    // Find assessment matching assessmentId
    let matchedAssessment: any = null;
    let isSavilleAssessment = false;
    // true when 360 self completes but rater threshold is not yet met — API still fires, card stays active
    let isSelf360BelowThreshold = false;
    if(urlAssessmentId && urlAssessmentId !== '' && (callbackAssessmentStatusForHogan === 'Completed' || callbackAssessmentStatusForHogan === '') ){ 
      finalUpdatedData?.forEach((items: any) => {
        const foundAssessment = items?.allassessmentsList.find((item: any) => {
          if(item?.sourceName === 'Envisia' && item?.assessmentDetails?.source?.focus360Id === urlAssessmentId ) {
            return item;
          } else if ( item?.assessmentId === urlAssessmentId ){
            return item;
          }
          return false;
        });
        if (foundAssessment) {
          if(foundAssessment?.category !== '360'){
            let projectAssignmentsData = {
              projectId: foundAssessment?.assessmentDetails?.projectId,
              leaderProfileId: foundAssessment?.assessmentDetails?.leaderProfileId,
              assessments: [
                {
                  "assessmentStructureId": foundAssessment?.assessmentStructureId,
                  "assignmentStatus": foundAssessment?.sourceName === 'Hogan' ? "Completed" : "Completed - Pending",
                }
              ]
            };
            //Adding condition in case of saville if user clicks Exit withing 2 min we should not update the status to completed.
            if ((foundAssessment?.sourceName === 'Saville' || foundAssessment?.sourceName === 'saville') && getMinutesDifference(foundAssessment?.assessmentDetails?.lastUpdatedTs) < 2) {
              isSavilleAssessment = true;
            } else {
              Api.updateProjectAssignmentAssessmentsStatus(projectAssignmentsData, foundAssessment.assignmentId)
              .then((res:any)=>{})
            }  
          } else {
            const raterType = foundAssessment?.assessmentDetails?.source?.raterType;
            const isSelfRater = raterType === 'Self' || raterType === 'self';
            // mark flag so the card is not moved to done, but still fall through to call the API
            if (isSelfRater && !foundAssessment?.isThresholdMet && foundAssessment?.survey?.allowToSelectRaters) {
              isSelf360BelowThreshold = true;
            }
            let bulkRatersUpdateData: any = {
              "assessments":[{
              "assessmentStructureId": foundAssessment?.assessmentDetails?.assessmentStructureId,
              ...(isSelfRater && { "appraisalStatus": "Completed - Pending" }),
              "raters": [
                  {
                    "raterEmail": foundAssessment?.raterEmail,
                    "status": "Completed - Pending",
                    "raterFirstName": foundAssessment?.raterFirstName,
                    "raterLastName": foundAssessment?.raterLastName,
                    "raterProfileId": foundAssessment?.raterProfileId,
                    "type": raterType,
                  }
                ]
              }],
               ...(!isSelfRater && { "allow_rater_update": true }),
            }
            Api.updateBulkRatersStatus(bulkRatersUpdateData, foundAssessment.assignmentId)
            .then((res:any)=>{})
          }
          
          matchedAssessment = (!isSavilleAssessment && !isSelf360BelowThreshold) ? {
            projectDetails: items?.projectDetails,
            assessment: foundAssessment
          } : null;
        }
      });
    }

    let filteredClosedAsses: any = [];
    finalUpdatedData?.forEach((items: any, key: any) => {
      const filteredData = items?.allassessmentsList.filter((item: any) => {
        const shouldIgnoreCloseDate = item?.ignoreCloseDate === true || items?.projectDetails?.ignoreCloseDate === true;
        const isProjectManuallyClosed = ['C', 'Closed', 'closed'].includes(items?.projectDetails?.status);

        if (item?.category === 'CBI') {
          return false;
        }

        const strippedTodaysDate = stripTime(new Date());
        const dueDate = item?.dueDate ? new Date(item.dueDate) : null;
        const strippedDueDate = dueDate && !isNaN(dueDate.getTime()) ? stripTime(dueDate) : null;
        // A manually closed project only makes an assessment overdue when it has no future due date of its own.
        const isOverdueAssessment = strippedDueDate ? strippedDueDate < strippedTodaysDate : isProjectManuallyClosed;

        // Projects flagged to ignore close dates, or assessments with a still-future due date, keep assessments in the Active section.
        if (shouldIgnoreCloseDate || !isOverdueAssessment) {
          return false;
        }
        
        // Determine assessment status based on assessment type
        let assessmentStatus;
        if (item?.category === '360') {
          if (item?.raterType && item?.raterType !== 'Self' && item?.raterType !== 'self') {
            assessmentStatus = item?.raterAssessmentStatus;
          } else {
            assessmentStatus = item?.appraisalStatus || item?.assessmentDetails?.source?.status;
          }
        } else {
          assessmentStatus = item?.assessmentDetails?.source?.status;
        }
        
        // Exclude completed and completed-pending status
        if (completedStatus.includes(assessmentStatus) || completedPendingStatus.includes(assessmentStatus)) {
          return false;
        }
        
        // Include only Not Started, In Progress, and Raters Invited
        return notStartedStatus.includes(assessmentStatus) || 
               inProgressStatus.includes(assessmentStatus) || 
               ratersInvitedStatus.includes(assessmentStatus);
      });
      pushIfNotEmpty(filteredClosedAsses, items, filteredData);
    });

    //Get Completed Assessments List
    let filteredCompletedAsses: any = [];
    finalUpdatedData?.forEach((items: any, key: any) => {
      const filteredData = items?.allassessmentsList.filter((item: any) => {
        if (item?.category === '360') {
          const isAssessmentCompleted = item?.assessmentDetails?.source?.status === 'Completed' || item?.assessmentDetails?.source?.status === 'Completed - Pending' || item?.assessmentDetails?.source?.status === 'C' || item?.appraisalStatus === 'Completed - Pending' || item?.appraisalStatus === 'Completed';
          if(item?.assessmentDetails?.source?.raterType && item?.assessmentDetails?.source?.raterType !== 'Self' && item?.assessmentDetails?.source?.raterType !== 'self'){
            return completedStatus.includes(item?.raterAssessmentStatus) || completedPendingStatus.includes(item?.raterAssessmentStatus);
          }

          if ((item?.assessmentDetails?.source?.raterType && (item?.assessmentDetails?.source?.raterType === 'Self' || item?.assessmentDetails?.source?.raterType === 'self'))  && isAssessmentCompleted && !item?.survey?.allowToSelectRaters) {
            return completedStatus.includes(item?.appraisalStatus) || completedPendingStatus.includes(item?.appraisalStatus);
          }

          if (item?.isThresholdMet && isAssessmentCompleted) {
            return completedStatus.includes(item?.appraisalStatus) || completedPendingStatus.includes(item?.appraisalStatus);
          }

          if(items?.projectDetails?.closeDate && new Date(items?.projectDetails?.closeDate) < new Date()) {
            return completedStatus.includes(item?.appraisalStatus) || completedPendingStatus.includes(item?.appraisalStatus);
          }
          return false;
        } else {
          return completedStatus.includes(item?.assessmentDetails?.source?.status) ||
               completedStatus.includes(item?.appraisalStatus) ||
               completedPendingStatus.includes(item?.assignmentStatus) ||
               completedStatus.includes(item?.assignmentStatus);
        }
      });
      pushIfNotEmpty(filteredCompletedAsses, items, filteredData);
    });



    //Get Assigned Assessments List
    let filteredAssignedAsses: any = [];
    finalUpdatedData?.forEach((items: any) => {
      const filteredData = items?.allassessmentsList.filter((item: any) => {
        const todaysDate = new Date();
        const strippedTodaysDate = stripTime(todaysDate);
        const dueDate = item?.dueDate ? new Date(item.dueDate) : null;
        const strippedDueDate = dueDate && !isNaN(dueDate.getTime()) ? stripTime(dueDate) : null;
        const shouldIgnoreCloseDate = item?.ignoreCloseDate === true || items?.projectDetails?.ignoreCloseDate === true;
        const isProjectManuallyClosed = ['C', 'Closed', 'closed'].includes(items?.projectDetails?.status);
        // A manually closed project only makes an assessment overdue when it has no future due date of its own.
        const isOverdueAssessment = strippedDueDate ? strippedDueDate < strippedTodaysDate : isProjectManuallyClosed;

        // Overdue assessments stay actionable only while the project ignores close dates.
        if (!shouldIgnoreCloseDate && isOverdueAssessment) {
          return false;
        }
        
        const sourceStatus = item?.assessmentDetails?.source?.status;
        const activeStatuses = [...notStartedStatus, ...inProgressStatus, ...notInvitedStatus, ...ratersInvitedStatus];
        const finishedStatuses = [...completedPendingStatus, ...completedStatus];
        const isNotCBI = item?.category !== 'CBI';
        const hasActiveSourceStatus = activeStatuses.includes(sourceStatus);
        let isNotFinished = true;
        const raterType = item?.assessmentDetails?.source?.raterType;
        const isAssessmentCompleted = item?.assessmentDetails?.source?.status === 'Completed' || item?.assessmentDetails?.source?.status === 'Completed - Pending' || item?.assessmentDetails?.source?.status === 'C';
        const isSelfRater = raterType === 'Self' || raterType === 'self';
        const projectCloseDate = stripTime(new Date(items?.projectDetails?.closeDate));
        const isProjectClosed = projectCloseDate.getTime() > strippedTodaysDate.getTime();
        if (shouldIgnoreCloseDate) {
          return isNotCBI && (hasActiveSourceStatus || shouldKeepCompletedSelfThreeSixtyActive(item)) && isNotFinished;
        }
        if (item?.category === '360' && isSelfRater && isProjectClosed ) {
          // Exclude if the underlying source status is a finished status
          if ((finishedStatuses.includes(sourceStatus) || finishedStatuses.includes(item?.appraisalStatus)) && !item?.survey?.allowToSelectRaters) {
            return false;
          }
          
          if (!item?.isThresholdMet || !isAssessmentCompleted) {
            return item;
          }
        } else {
          isNotFinished = !finishedStatuses.includes(item?.assignmentStatus) &&
          !completedStatus.includes(item?.appraisalStatus) &&
          !completedPendingStatus.includes(item?.appraisalStatus) &&
          !completedPendingStatus.includes(item?.raterAssessmentStatus);
        }                  
        
        return isNotCBI && hasActiveSourceStatus && isNotFinished;
      });
      pushIfNotEmpty(filteredAssignedAsses, items, filteredData);
    });

    // Add matchedAssessment to filteredCompletedAsses if found
    if (matchedAssessment && matchedAssessment !== null) {
      const existingProject = filteredCompletedAsses.find((proj: any) => 
        proj.projectDetails?.id === matchedAssessment.projectDetails?.id
      );
      
      if (existingProject) {
        if(!existingProject?.allassessmentsList.find((item: any) => (item?.assessmentId === matchedAssessment.assessment?.assessmentId || (item?.sourceName === 'Envisia' && item?.assessmentDetails?.source?.focus360Id === matchedAssessment.assessment?.assessmentDetails?.source?.focus360Id)))) {
          existingProject.allassessmentsList.push(matchedAssessment.assessment);
        }
      } else {
        filteredCompletedAsses.push({
          projectDetails: matchedAssessment.projectDetails,
          allassessmentsList: [matchedAssessment.assessment]
        });
      }
    }

    // Filter out assessment matching assessmentId from filteredAssignedAsses
    if(urlAssessmentId && urlAssessmentId !== '' && !isSelf360BelowThreshold && (callbackAssessmentStatusForHogan === 'Completed' || callbackAssessmentStatusForHogan === '')){ 
      filteredAssignedAsses = filteredAssignedAsses.map((project: any) => ({
        ...project,
        allassessmentsList: (!isSavilleAssessment) ? 
        project.allassessmentsList.filter((item: any) => {
          if(item?.sourceName === 'Envisia' && item?.assessmentDetails?.source?.focus360Id === urlAssessmentId){
            return false;
          } else if(item?.assessmentId !== urlAssessmentId) {
            return item;
          }
          return false;
        }) : project.allassessmentsList
      })).filter((project: any) => project.allassessmentsList.length > 0);
    }

     filteredClosedAsses?.forEach((item: any) => {
      item?.allassessmentsList.sort((a: any, b: any) => {
        const dateA = new Date(a?.dueDate);
        const dateB = new Date(b?.dueDate);

        if (dateA > dateB) return -1;
        if (dateA < dateB) return 1;

        return a?.title.localeCompare(b?.title);
      });
    });
    setClosedAssessArr(filteredClosedAsses);

    filteredCompletedAsses?.forEach((item: any) => {
      item?.allassessmentsList.sort((a: any, b: any) => {
        const dateA = new Date(a?.lastUpdatedTs);
        const dateB = new Date(b?.lastUpdatedTs);

        if (dateA > dateB) return -1;
        if (dateA < dateB) return 1;

        return a?.title.localeCompare(b?.title);
      });
    });
    setCompletedAssessArr(filteredCompletedAsses);
    
    // Merge assessments across all projects, then sort by instrument order
    const allAssignedAssessments = filteredAssignedAsses?.flatMap((item: any) => item?.allassessmentsList || []) || [];

    allAssignedAssessments.sort(compareAssessmentCards);

    if (allAssignedAssessments.length === 0) return [];
    // Single merged project bucket so cards render in one combined, globally sorted group
    return [{ projectDetails: filteredAssignedAsses?.[0]?.projectDetails, allassessmentsList: allAssignedAssessments }];
  };

  const getAssessmentStructureDetails = async (flattenedAssignments: any, assessStructureData: any, leaderProfileId: any, leaderEmail: string) => {
    await Api.getLeaderAssessmentDetails(globalState?.clientData?.id, leaderProfileId)
      .then(async (response: any) => {
        if (response?.success) {
          const assessmentData = response?.response?.data?.data;
          assessmentData.sort((value1: any, value2: any) => value1?.lastUpdatedTs - value2?.lastUpdatedTs);
          const clientId = globalState?.clientData?.id;
          assessmentData.forEach((data: any) => {
            flattenedAssignments?.forEach((element: any) => {
              if (element?.allassessmentsList && Object.keys(element?.allassessmentsList)?.length > 0) {
                Object.keys(element?.allassessmentsList).forEach((key: any) => {
                  if (element?.allassessmentsList[key]?.assessmentId === data?.id ) {
                    if (element.allassessmentsList[key]?.raters && element?.allassessmentsList[key]?.survey?.allowToSelectRaters) {
                      const listOfRaters = element.allassessmentsList[key]?.raters;
                      const listOfRatersRequired = element.allassessmentsList[key]?.ratersRequired;

                      const getRatersCount = listOfRaters?.reduce((acc: any, currentItem: any) => {
                        const value = currentItem['type'];
                        acc[value] = (acc[value] || 0) + 1;
                        return acc;
                      }, {});
                      let countThreshold = 0;
                      Object.keys(listOfRatersRequired).forEach((item: any) => {
                        if (getRatersCount[listOfRatersRequired[item]?.type] >= listOfRatersRequired[item]?.threshold) {
                          countThreshold += 1;
                        }
                      });
                      if (Object.keys(listOfRatersRequired)?.length <= countThreshold && (notStartedStatus.includes(data?.source?.status) || notInvitedStatus.includes(data?.source?.status))) {
                        data.source.status = 'Raters Invited';
                      }

                      if (Object.keys(listOfRatersRequired)?.length <= countThreshold && (completedStatus.includes(data?.source?.status) || completedPendingStatus.includes(data?.source?.status))) {
                        element.allassessmentsList[key]['isThresholdMet'] = true;
                      } else {
                        element.allassessmentsList[key]['isThresholdMet'] = false;
                      }
                    }
                    element.allassessmentsList[key]['assessmentDetails'] = data;
                  }

                  const match = assessStructureData?.find((structureItem: any) => structureItem?.id === element?.allassessmentsList[key]?.assessmentStructureId);
                  if (match) {
                    Object.assign(element.allassessmentsList[key], { ...match });
                  }
                })
              }
            });
          });

          //Fet Raters Assessment Details
          await Api.getRaterAssessment(clientId, leaderEmail)
            .then((response: any) => {
              if (response?.success) {
                const assessmentData = response?.response?.data?.data;
                assessmentData.sort((value1: any, value2: any) => value1?.lastUpdatedTs - value2?.lastUpdatedTs);
                assessmentData.forEach((data: any) => {
                  flattenedAssignments?.forEach((element: any) => {
                    if (element?.allassessmentsList && Object.keys(element?.allassessmentsList)?.length > 0) {
                      Object.keys(element?.allassessmentsList).forEach((key: any) => {
                        if (element?.allassessmentsList[key]?.assessmentId === data?.id && !element?.allassessmentsList[key]?.raters) {
                          element.allassessmentsList[key]['assessmentDetails'] = data;
                        }

                        let match = assessStructureData?.find((structureItem: any) => structureItem?.id === element?.allassessmentsList[key]?.assessmentStructureId);
                        if (match) {
                          const raterType = element.allassessmentsList[key]?.assessmentDetails?.source?.raterType;
                          if (raterType != null && raterType !== undefined && raterType !== 'self' && raterType !== 'Self') {
                            const leaderFirstName = element.allassessmentsList[key]?.leaderDetails?.leaderFirstName || '';
                            const leaderLastName = element.allassessmentsList[key]?.leaderDetails?.leaderLastName || '';
                            match = {
                              ...match,
                              'title': match?.title,
                              'leaderName': leaderFirstName + ' ' + leaderLastName,
                              'description': 'Please complete survey for ' + leaderFirstName + ' ' + leaderLastName + ' as part of 360 assessment',
                            };
                          }
                          Object.assign(element.allassessmentsList[key], { ...match });
                        }
                      })
                    }
                  });
                });
              }
            });
          const filteredAssignedAsses = filteredCompletedAssessments(flattenedAssignments);
          updateAssessmentStatus(filteredAssignedAsses);
        }
        setShowLoader(false);
        checkUnauthorized(response, dispatch);
      })
      .catch((err: any) => {
        setShowLoader(false);
        checkAuthErr(err, dispatch);
      });
  };

  const updateAssessmentStatus = (listOfAssessments: any) => {
    // Set assigned data; counts are derived by useEffect
    setAssignedAssessments(listOfAssessments);
  };

  useEffect(() => {
    const count = { all: 0, completed: 0 } as any;
    assignedAssessments.forEach((project: any) => {
      Object.values(project?.allassessmentsList || {}).forEach((assessment: any) => {
        count.all += 1;
      });
    });
    count.completed = completedAssessArr.reduce((sum: number, proj: any) => sum + (proj?.allassessmentsList?.length || 0), 0);
    count.closed = closedAssessArr.reduce((sum: number, proj: any) => sum + (proj?.allassessmentsList?.length || 0), 0);

    setAssessmentCounts(count);
  }, [assignedAssessments, completedAssessArr, closedAssessArr]);

  return (
    <>
      {showLoader && <Loader />}
      <div className='mainWrapper' style={{height: (assessmentCounts?.all > 3) ? '100%': '100vh'}}>
        <div className='dashboardPage'>
          <AuthHeader title={title} headerLogo={headerLogo} setLoader={setShowLoader} />
          <div className='middleWrapper'>{/*style={{ backgroundImage: (isMobileTab || isMobileTabLand) ? `url(${headerImage})` : 'none' }}*/}
            <div className='dashboardWelcomeText'>
                <div className='authHeaderBannerContent'>
                  
                  <p className='authHeaderBannerWelcomeContent'>Welcome, {leaderName}.<span className='authHeaderBannerWelcomeContentBold'> You have {(assessmentCounts?.all ?? 0)} {(assessmentCounts?.all === 1 ? 'assignment' : 'assignments')}.</span></p>
                </div>

                <div className='completedAssessment'>
                  <Button
                    text={isLarge ? "Show completed/closed\nassignments and reports" : "Show completed/closed assignments and reports"}
                    variant={ButtonVariant.Secondary}
                    classNames='completedAssessmentButton'
                    onClick={() => setShowModal(true)}
                    transparent={true}
                    iconProps={{ path: IconName.mdiChevronRight }}
                    alignIcon={ButtonIconAlign.Right}
                  >
                  </Button>
                </div>
            </div>

            <div className='dashboardContentWrapper'>
              <div className='dashboardBackgroundImage' style={{backgroundImage: `url(${headerImage})`}}>
                {/* <img src={headerImage} className='dashboardBGImg' alt="background-image"/> */}
                <div className='dashboardBGOverlay'>
                  <div className='dashboardContent' ref={cardWrapRef}>
                    {assignedAssessments?.length > 0 ? (
                      assignedAssessments?.map((project: any) => (
                        Object.keys(project?.allassessmentsList).map((item: any) => (
                          <GenerateCards
                            key={`${project?.projectDetails?.id}-${project?.allassessmentsList[item]?._id || item}`}
                            cardId={`${project?.projectDetails?.id}-${project?.allassessmentsList[item]?._id || item}`}
                            projectEndDate={project?.allassessmentsList[item]?.projectDetails?.closeDate}
                            leaderData={project?.allassessmentsList[item]?.leaderDetails}
                            assessmentsData={project?.allassessmentsList[item]}
                            isMobile={isMobile}
                            navigate={navigate}
                            dispatch={dispatch}
                            clientDetails={globalState?.clientData}
                            assessmentCounts={assessmentCounts}
                          />
                        ))
                      ))
                    ):(
                      <div className='emptyStateWrapper'>
                        <div className='emptyIconWrap'>
                          <img src={(assessmentCounts?.completed > 0) ? noActiveAssessmentImg : noAssessmentsAssignedImg} className='emptyDrawer' alt="empty-drawer"/>
                        </div>
                        <div className='emptyTextTitle'>
                          {(assessmentCounts?.completed > 0) ? noActiveAssessments : noAssessmentsAssigned}
                        </div>
                        <div className='emptyTextSubtitle'>
                          {(assessmentCounts?.completed > 0) ? noActiveAssessmentsDesc : noAssessmentsAssignedDesc}
                        </div>
                        {assessmentCounts?.completed > 0 && (
                          <div className='emptyActionWrap'>
                            <Button
                              text={'Show completed/closed assignments and reports'}
                              variant={ButtonVariant.Secondary}
                              classNames='actionButton'
                              style={actionButtonStyle}
                              onClick={() => setShowModal(true)}
                              iconProps={{ path: IconName.mdiArrowRight }}
                              alignIcon={ButtonIconAlign.Right}
                            />
                          </div>
                        )}
                      </div>
                    )}
                    {/* :(<>{assLoader && <AssessmentsLoader />}</>) */}
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showModal && (
          <CompletedAssessmentsTableModal
            showModal={showModal}
            bodyText=''
            showCancelButton={false}
            onClose={() => setShowModal(false)}
            data={completedAssessArr.flatMap((project: any) => 
              Object.values(project.allassessmentsList).map((item: any) => {
                const isRaterAssessment = item?.category === '360' && item?.assessmentDetails?.source?.raterType && item?.assessmentDetails?.source?.raterType.toLowerCase() !== 'self';
                if (isRaterAssessment) {
                  const surveyName = item?.survey?.surveyNameRaters;
                  const leader = item?.leaderDetails;
                  const focusName = leader ? `${leader.leaderFirstName || ''} ${leader.leaderLastName || ''}`.trim() : (item?.leaderName || '');
                  const nameToShow = surveyName ? `${surveyName} ${focusName}` : (item?.title || '');
                  return {
                    title: nameToShow,
                    completedDate: item?.assessmentDetails?.lastUpdatedTs,
                    assessmentType: item?.category
                  };
                }

                return {
                  title: getAssessmentDisplayTitle(item),
                  completedDate: item?.assessmentDetails?.lastUpdatedTs,
                  assessmentType: item?.category
                };
              })
            )} 
            closedAssessmentdata={closedAssessArr.flatMap((project: any) => 
              Object.values(project.allassessmentsList).map((item: any) => {
                const isRaterAssessment = item?.category === '360' && item?.assessmentDetails?.source?.raterType && item?.assessmentDetails?.source?.raterType.toLowerCase() !== 'self';
                if (isRaterAssessment) {
                  const surveyName = item?.survey?.surveyNameRaters;
                  const leader = item?.leaderDetails;
                  const focusName = leader ? `${leader.leaderFirstName || ''} ${leader.leaderLastName || ''}`.trim() : (item?.leaderName || '');
                  const nameToShow = surveyName ? `${surveyName} ${focusName}` : (item?.title || '');
                  return {
                    title: nameToShow,
                    dueDate: item?.dueDate,
                    assessmentType: item?.category
                  };
                }

                return {
                  title: getAssessmentDisplayTitle(item),
                  dueDate: item?.dueDate,
                  assessmentType: item?.category
                };
              })
            )} 
            documentsData={documentsData}
            documentsCount={documentsData?.length || 0}
            closedAssessCount={assessmentCounts?.closed} 
            completedAssessCount={assessmentCounts?.completed}                   
          />
          )}
          <AuthFooter />
        </div>
      </div>
    </>
  );
};

export default DashBoard;
